import React from "react";
import { useTranslation } from "react-i18next";
import { Droplets, Loader2, Plus, ShieldCheck, Sparkles, Trash2, Sun } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ENHANCE_PHOTOS_MAX, MARK_KINDS, MARK_MAX, MARK_SECTIONS, MARK_WHAT_MAX, inkPictureCount, markPlace, sectionCounts, sectionOf,
  sectionPlaces, type MarkSection,
} from "@/shared/face-qc/marks.mjs";
import { planTotal, rescanPlan } from "@/shared/face-qc/index.mjs";
import {
  faceLockErrorText, INK_MAX_EDGE, newIdempotencyKey, startFaceLockEnhance, uploadFaceLockPhotos, FaceLockUploadError,
  type EnhanceKind, type EnhancePhoto, type FaceLockAvatar, type FaceLockMark, type MarkKind,
} from "@/lib/facelock";
import { captionFor } from "@/lib/face-scan/caption";
import { emptySet, framesToFiles, orderedFrames, withPlan, type ScanSet } from "@/lib/face-scan/session";
import type { FlowResult } from "../scan/ScanFlow";
import InkScan, { type InkMark, type InkShot } from "./InkScan";

// "Enhance this avatar": FaceLock Ink Scan and FaceShot SharpShoot (owner
// direction 2026-10-06: supplemental scans that only ever improve the avatar).
//
//   1. Choose: the Ink Scan (tattoos, scars, moles, birthmarks: each named,
//      then captured up close) and/or the SharpShoot (the scan again in
//      another moment: another day, light, outfit and place).
//   2. Ink Scan: which PARTS of the body now (head, face and neck, arms and
//      hands, torso, legs and feet: only the ones that will show in what
//      the person is making; owner direction 2026-10-06, "some of us are
//      literally covered"), then what each mark there is and where it is on
//      the person's own body. A part scanned before says so; scanning a
//      place again replaces that mark; every other mark the avatar has stays.
//      SharpShoot: which parts, and what they are wearing.
//   3. The scans themselves (full screen), then upload and send.
//   4. Sent: the avatar keeps working exactly as it is; when the result is
//      learned, its card offers the before-and-after, and nothing changes
//      unless the person keeps the result (EnhanceCompare).
//
// The scans are kept only while this dialog is open; closing it before
// sending discards them (nothing is uploaded until Send).

const loadScanFlow = () => import("../scan/ScanFlow");
const ScanFlow = React.lazy(loadScanFlow);
/**
 * Everything the scans load later, fetched when the dialog opens: a deploy
 * while the person is scanning removes the old build's chunks, and a chunk
 * that will not load reloads the page (lib/stale-bundle.ts), losing every
 * picture taken so far (owner, 2026-10-07: the Ink Scan done, then an error
 * and back to the ChopShop). Fetched up front, a stale page reloads at the
 * start, before any picture, and never mid-scan (a failure is left
 * unhandled on purpose: the stale-bundle guard hears it and reloads).
 */
function preloadScans() {
  void loadScanFlow();
  void import("@/lib/face-scan/vision");
}

type Step = "choose" | "details" | "ink" | "sharpshoot" | "send" | "sent";
type Part = "face" | "upper" | "full";
type MarkDraft = { place: string; kind: MarkKind; what: string; always: boolean };

const blankMark = (): MarkDraft => ({ place: "", kind: "tattoo", what: "", always: true });

/** What each choice costs in pictures, for the words of the 48-picture check. */
const PICTURES_EACH = {
  body: inkPictureCount(["chest"]),
  head: inkPictureCount(["forehead"]),
  face: planTotal(rescanPlan(["face"])),
  upper: planTotal(rescanPlan(["upper"])),
  full: planTotal(rescanPlan(["full"])),
};

export default function EnhanceDialog({ avatar, open, onOpenChange, onStarted }: {
  avatar: FaceLockAvatar | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sent: read the list again (the card shows the enhancement running). */
  onStarted: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = React.useState<Step>("choose");
  const [kinds, setKinds] = React.useState<EnhanceKind[]>(["ink", "sharpshoot"]);
  const [marks, setMarks] = React.useState<MarkDraft[]>([blankMark()]);
  const [sections, setSections] = React.useState<MarkSection[]>([]);
  const [parts, setParts] = React.useState<Part[]>(["face", "upper"]);
  const [outfit, setOutfit] = React.useState("");
  const [inkShots, setInkShots] = React.useState<InkShot[] | null>(null);
  const [sharpshootSet, setSharpshootSet] = React.useState<ScanSet>(() => withPlan(emptySet(), rescanPlan(["face", "upper"])));
  const [sharpshootDone, setSharpshootDone] = React.useState(false);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState("");
  const uploaded = React.useRef(new Map<File, string>());
  const filesRef = React.useRef<{ sharpshoot: File[]; ink: File[] }>({ sharpshoot: [], ink: [] });
  const keyRef = React.useRef(newIdempotencyKey());
  const avatarId = avatar?.id || "";
  const subject = avatar?.enhance?.subject === "woman" ? "woman" : "man";

  // The draft is kept while the dialog is closed and opened again for the same avatar (owner, 2026-10-08: he filled it
  // all in, tapped outside the box, and it was gone); it starts clean for another avatar, or once one was sent.
  const draftFor = React.useRef<string | null>(null);
  const sentRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) return;
    preloadScans();
    if (draftFor.current === avatarId && !sentRef.current) return;
    draftFor.current = avatarId;
    sentRef.current = false;
    setStep("choose");
    setKinds(["ink", "sharpshoot"]);
    setMarks([blankMark()]);
    setSections([]);
    setParts(["face", "upper"]);
    setOutfit("");
    setInkShots(null);
    setSharpshootSet(withPlan(emptySet(), rescanPlan(["face", "upper"])));
    setSharpshootDone(false);
    setBusy(null);
    setError("");
    uploaded.current = new Map();
    filesRef.current = { sharpshoot: [], ink: [] };
    keyRef.current = newIdempotencyKey();
  }, [open, avatarId]);

  // The Ink Scan's marks, the same array while the drafts are (the crew list re-renders this dialog every 15 s while an
  // avatar is busy; the scan keys its views on the marks).
  const inkMarks = React.useMemo<InkMark[]>(() => marks.filter((m) => m.place)
    .map((m, n) => ({ id: `m${n + 1}`, place: m.place, kind: m.kind, what: m.what.trim(), always: m.always })), [marks]);
  if (!avatar) return null;
  const wantInk = kinds.includes("ink");
  const wantSharpshoot = kinds.includes("sharpshoot");
  const cleanMarks = marks.filter((m) => m.place);
  const marksOk = !wantInk || (cleanMarks.length > 0 && cleanMarks.length <= MARK_MAX
    && new Set(cleanMarks.map((m) => m.place)).size === cleanMarks.length && cleanMarks.every((m) => m.what.length <= MARK_WHAT_MAX));
  // Every picture this enhancement will send, counted BEFORE any scan starts (owner direction 2026-10-07: 12
  // marks a scan). Twelve body marks and a SharpShoot are more than the server takes (ENHANCE_PHOTOS_MAX), and
  // it would refuse them only after minutes of scanning and the whole upload: the person is told now what to
  // take off.
  const pictures = (wantInk ? inkPictureCount(cleanMarks.map((m) => m.place)) : 0)
    + (wantSharpshoot && parts.length ? planTotal(rescanPlan(parts)) : 0);
  const picturesOk = pictures <= ENHANCE_PHOTOS_MAX;
  const detailsOk = marksOk && (!wantSharpshoot || parts.length > 0) && picturesOk;
  const price = avatar.enhance?.price ?? 0;
  // The marks the avatar has from earlier Ink Scans, by part of the body.
  const onFile = avatar.enhance?.marks || [];
  const onFileAt = (place: string) => onFile.find((m) => m.place === place) || null;
  const counts = sectionCounts(onFile);
  const onFileHere = onFile.filter((m) => sections.includes(sectionOf(m.place) as MarkSection));

  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const setMark = (n: number, patch: Partial<MarkDraft>) => setMarks((all) => all.map((m, i) => {
    if (i !== n) return m;
    const next = { ...m, ...patch };
    if (patch.place !== undefined) {
      // A place the avatar already has a mark at: start from what it knows (this scan replaces it).
      const had = onFileAt(patch.place);
      if (had) Object.assign(next, { kind: had.kind, what: next.what || had.what });
      // "Shows in most outfits" is the place's, never the person's choice (owner direction 2026-10-07).
      next.always = markPlace(patch.place)?.always ?? true;
    }
    return next;
  }));
  const toggleSection = (sec: MarkSection) => {
    const on = sections.includes(sec);
    setSections((all) => toggle(all, sec));
    // A part taken off the list takes its rows with it.
    if (on) setMarks((all) => {
      const left = all.filter((m) => !m.place || sectionOf(m.place) !== sec);
      return left.length ? left : [blankMark()];
    });
  };

  const startScans = () => {
    setError("");
    if (wantSharpshoot) setSharpshootSet(withPlan(emptySet(), rescanPlan(parts)));
    setStep(wantInk ? "ink" : "sharpshoot");
  };

  const afterInk = (shots: InkShot[]) => {
    setInkShots(shots);
    filesRef.current.ink = shots.map((s, i) => new File([s.blob], `facelock-ink-${String(i + 1).padStart(2, "0")}-${s.mark}-${s.view}.jpg`,
      { type: s.blob.type || "image/jpeg" }));
    setStep(wantSharpshoot ? "sharpshoot" : "send");
  };

  const afterSharpshoot = async (out: FlowResult): Promise<string | null> => {
    if (out.kind !== "set") return null;
    setSharpshootSet(out.set);
    filesRef.current.sharpshoot = framesToFiles(out.set);
    setSharpshootDone(true);
    setStep("send");
    return null;
  };

  const send = async () => {
    if (busy) return;
    setError("");
    const sharpshootFrames = wantSharpshoot ? orderedFrames(sharpshootSet) : [];
    const sharpshootFiles = wantSharpshoot ? filesRef.current.sharpshoot : [];
    const inkFiles = wantInk ? filesRef.current.ink : [];
    const all = [...sharpshootFiles, ...inkFiles];
    const inkSet = new Set(inkFiles);
    let urls: string[];
    try {
      urls = await uploadFaceLockPhotos(all, uploaded.current, (n, total) => setBusy(t("faceLock.create.uploading", { n, total })),
        (f) => (inkSet.has(f) ? INK_MAX_EDGE : undefined));
    } catch (e) {
      setBusy(null);
      setError(e instanceof FaceLockUploadError ? t("faceLock.enhance.uploadFailed") : t("faceLock.errors.generic"));
      return;
    }
    const photos: EnhancePhoto[] = [
      ...sharpshootFrames.map((f, i) => ({ url: urls[i], bucket: f.bucket, caption: captionFor(f, subject) })),
      ...(inkShots || []).map((s, i) => ({ url: urls[sharpshootFiles.length + i], mark: s.mark, view: s.view,
        ...(s.box ? { box: s.box } : {}) })),
    ];
    setBusy(t("faceLock.enhance.sending"));
    const res = await startFaceLockEnhance(avatar.id, {
      kinds,
      marks: wantInk ? inkMarks.map(({ id: _id, ...m }) => m as FaceLockMark) : [],
      parts: wantSharpshoot ? parts : [],
      outfit: wantSharpshoot ? outfit.trim() : "",
      photos,
      idempotency_key: keyRef.current,
    });
    setBusy(null);
    if (res.ok === false) { setError(faceLockErrorText(t, res)); return; }
    sentRef.current = true;
    setStep("sent");
    onStarted();
  };

  const closable = !busy && step !== "ink" && step !== "sharpshoot";

  return (
    <>
      <Dialog open={open && step !== "ink" && step !== "sharpshoot"} onOpenChange={(o) => { if (closable) onOpenChange(o); }}>
        <DialogContent overlayClassName="z-[400]" className="z-[400] max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="enhance-dialog" data-step={step}
          onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t("faceLock.enhance.title", { name: avatar.name })}</DialogTitle>
            <DialogDescription>{t("faceLock.enhance.lead")}</DialogDescription>
          </DialogHeader>

          {step === "choose" && (
            <div className="grid gap-3">
              {(["ink", "sharpshoot"] as const).map((k) => {
                const on = kinds.includes(k);
                return (
                  <button key={k} type="button" role="checkbox" aria-checked={on} data-testid={`enhance-kind-${k}`}
                    onClick={() => setKinds((all) => toggle(all, k))}
                    className={`grid gap-1 rounded-lg border p-3 text-left transition ${on ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary/60"}`}>
                    <span className="flex items-center gap-2 font-semibold">
                      {k === "ink" ? <Droplets className="h-4 w-4" aria-hidden /> : <Sun className="h-4 w-4" aria-hidden />}
                      {t(`faceLock.enhance.kind.${k}.title`)}
                    </span>
                    <span className="text-sm text-muted-foreground">{t(`faceLock.enhance.kind.${k}.body`)}</span>
                  </button>
                );
              })}
              <p className="flex items-start gap-2 rounded-md bg-muted/50 p-2 text-sm" data-testid="enhance-guarantee">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden /> {t("faceLock.enhance.guarantee")}
              </p>
              <p className="text-sm text-muted-foreground" data-testid="enhance-price">
                {price > 0 ? t("faceLock.enhance.priceHeld", { count: price }) : t("faceLock.enhance.priceFree")}
              </p>
              <Button type="button" disabled={!kinds.length} data-testid="enhance-next" onClick={() => setStep("details")} className="w-fit">
                <Sparkles aria-hidden /> {t("faceLock.enhance.next")}
              </Button>
            </div>
          )}

          {step === "details" && (
            <div className="grid gap-4">
              {wantInk && (
                <fieldset className="grid gap-2" data-testid="enhance-marks">
                  <legend className="font-semibold">{t("faceLock.enhance.marksTitle")}</legend>
                  <p className="text-sm text-muted-foreground">{t("faceLock.enhance.sectionsLead")}</p>
                  <div className="grid gap-2 sm:grid-cols-2" data-testid="enhance-sections">
                    {MARK_SECTIONS.map((sec) => {
                      const on = sections.includes(sec);
                      return (
                        <button key={sec} type="button" role="checkbox" aria-checked={on} data-testid={`enhance-section-${sec}`}
                          onClick={() => toggleSection(sec)}
                          className={`grid gap-0.5 rounded-lg border px-3 py-2 text-left transition ${on ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary/60"}`}>
                          <span className="text-sm font-medium">{t(`faceLock.marks.section.${sec}`)}</span>
                          <span className="text-xs text-muted-foreground" data-testid={`enhance-section-status-${sec}`}>
                            {counts[sec] > 0 ? t("faceLock.enhance.sectionHave", { count: counts[sec] }) : t("faceLock.enhance.sectionNone")}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {sections.length > 0 && onFileHere.length > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="enhance-on-file">
                      {t("faceLock.enhance.onFileList", { list: onFileHere.map((m) => t(`faceLock.marks.place.${m.place}`)).join(", ") })}
                    </p>
                  )}
                  {sections.length > 0 && <p className="text-sm text-muted-foreground">{t("faceLock.enhance.marksLead")}</p>}
                  {sections.length > 0 && marks.map((m, n) => (
                    <div key={n} className="grid gap-2 rounded-lg border border-border p-2 sm:grid-cols-[1fr_1fr]" data-testid={`enhance-mark-${n}`}>
                      <label className="grid gap-1 text-sm">
                        <span>{t("faceLock.enhance.where")}</span>
                        <select value={m.place} onChange={(e) => setMark(n, { place: e.target.value })} data-testid={`enhance-mark-place-${n}`}
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                          <option value="">{t("faceLock.enhance.pickPlace")}</option>
                          {MARK_SECTIONS.filter((sec) => sections.includes(sec)).map((sec) => (
                            <optgroup key={sec} label={t(`faceLock.marks.section.${sec}`)}>
                              {sectionPlaces(sec).map((p) => (
                                <option key={p.id} value={p.id} disabled={marks.some((o, i) => i !== n && o.place === p.id)}>
                                  {t(`faceLock.marks.place.${p.id}`)}{onFileAt(p.id) ? ` ${t("faceLock.enhance.onFileMark")}` : ""}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </label>
                      <label className="grid gap-1 text-sm">
                        <span>{t("faceLock.enhance.whatKind")}</span>
                        <select value={m.kind} onChange={(e) => setMark(n, { kind: e.target.value as MarkKind })} data-testid={`enhance-mark-kind-${n}`}
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                          {MARK_KINDS.map((k) => <option key={k} value={k}>{t(`faceLock.marks.kind.${k}`)}</option>)}
                        </select>
                      </label>
                      {m.place && onFileAt(m.place) && (
                        <p className="text-xs text-muted-foreground sm:col-span-2" data-testid={`enhance-mark-replaces-${n}`}>{t("faceLock.enhance.replaces")}</p>
                      )}
                      <label className="grid gap-1 text-sm sm:col-span-2">
                        <span>{t("faceLock.enhance.what")}</span>
                        <Input value={m.what} maxLength={MARK_WHAT_MAX} placeholder={t("faceLock.enhance.whatExample")}
                          onChange={(e) => setMark(n, { what: e.target.value })} data-testid={`enhance-mark-what-${n}`} className="h-9" />
                      </label>
                      {marks.length > 1 && (
                        <button type="button" className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground underline sm:col-span-2"
                          onClick={() => setMarks((all) => all.filter((_, i) => i !== n))} data-testid={`enhance-mark-remove-${n}`}>
                          <Trash2 className="h-3.5 w-3.5" aria-hidden /> {t("faceLock.enhance.removeMark")}
                        </button>
                      )}
                    </div>
                  ))}
                  {sections.length > 0 && (marks.length < MARK_MAX ? (
                    <Button type="button" size="sm" variant="outline" className="w-fit" data-testid="enhance-mark-add"
                      onClick={() => setMarks((all) => [...all, blankMark()])}>
                      <Plus aria-hidden /> {t("faceLock.enhance.addMark")}
                    </Button>
                  ) : (
                    <p className="text-xs text-muted-foreground" data-testid="enhance-mark-max">{t("faceLock.enhance.marksMax", { count: MARK_MAX })}</p>
                  ))}
                </fieldset>
              )}
              {wantSharpshoot && (
                <fieldset className="grid gap-2" data-testid="enhance-sharpshoot">
                  <legend className="font-semibold">{t("faceLock.enhance.sharpshootTitle")}</legend>
                  <p className="text-sm text-muted-foreground">{t("faceLock.enhance.sharpshootLead")}</p>
                  <div className="flex flex-wrap gap-2">
                    {(["face", "upper", "full"] as const).map((p) => (
                      <label key={p} className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-sm">
                        <input type="checkbox" checked={parts.includes(p)} onChange={() => setParts((all) => toggle(all, p))} data-testid={`enhance-part-${p}`} />
                        {t(`faceLock.enhance.part.${p}`)}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid="enhance-sharpshoot-count">
                    {t("faceLock.enhance.sharpshootCount", { count: planTotal(rescanPlan(parts.length ? parts : ["face"])) })}
                  </p>
                  <label className="grid gap-1 text-sm">
                    <span>{t("faceLock.enhance.outfit")}</span>
                    <Input value={outfit} maxLength={60} placeholder={t("faceLock.enhance.outfitExample")}
                      onChange={(e) => setOutfit(e.target.value)} data-testid="enhance-outfit" className="h-9" />
                  </label>
                </fieldset>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={!detailsOk} data-testid="enhance-scan" onClick={startScans}>
                  {t(wantInk ? "faceLock.enhance.startInk" : "faceLock.enhance.startSharpshoot")}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setStep("choose")}>{t("faceLock.enhance.back")}</Button>
              </div>
              {!marksOk && wantInk && cleanMarks.length > 0 && <p className="text-sm text-amber-500">{t("faceLock.enhance.marksCheck")}</p>}
              {!picturesOk && (
                <p className="text-sm text-amber-500" role="status" data-testid="enhance-too-many">
                  {t("faceLock.enhance.tooManyPictures", { total: pictures, max: ENHANCE_PHOTOS_MAX, over: pictures - ENHANCE_PHOTOS_MAX, ...PICTURES_EACH })}
                </p>
              )}
            </div>
          )}

          {step === "send" && (
            <div className="grid gap-3" data-testid="enhance-send">
              <ul className="grid gap-1 text-sm">
                {wantInk && inkShots && <li>{t("faceLock.enhance.inkReady", { count: inkShots.length, marks: inkMarks.length })}</li>}
                {wantSharpshoot && sharpshootDone && <li>{t("faceLock.enhance.sharpshootReady", { count: orderedFrames(sharpshootSet).length })}</li>}
              </ul>
              <p className="flex items-start gap-2 text-sm"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden /> {t("faceLock.enhance.whatNext")}</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={!!busy} data-testid="enhance-send-button" onClick={() => void send()}>
                  {busy ? <><Loader2 className="animate-spin" aria-hidden /> {busy}</> : t("faceLock.enhance.send")}
                </Button>
                {!busy && (
                  <Button type="button" variant="ghost" onClick={() => setStep(wantInk ? "ink" : "sharpshoot")} data-testid="enhance-rescan">
                    {t("faceLock.enhance.scanAgain")}
                  </Button>
                )}
              </div>
            </div>
          )}

          {step === "sent" && (
            <div className="grid gap-3" data-testid="enhance-sent">
              <p>{t("faceLock.enhance.sent")}</p>
              <Button type="button" className="w-fit" onClick={() => onOpenChange(false)}>{t("faceLock.enhance.close")}</Button>
            </div>
          )}

          {error && <div role="alert" data-testid="enhance-error" className="text-sm text-red-500">{error}</div>}
        </DialogContent>
      </Dialog>

      {open && step === "ink" && (
        <InkScan marks={inkMarks} onDone={afterInk} onClose={() => setStep(inkShots ? "send" : "details")} />
      )}
      {open && step === "sharpshoot" && (
        <React.Suspense fallback={<div className="fixed inset-0 z-[420] flex items-center justify-center bg-[#0b0c0e] text-[#f4f5f7]"><Loader2 className="h-10 w-10 motion-safe:animate-spin" aria-label={t("faceLock.scan.loading")} /></div>}>
          <ScanFlow
            set={sharpshootSet}
            onSet={setSharpshootSet}
            onFinish={afterSharpshoot}
            onClose={() => setStep(sharpshootDone ? "send" : "details")}
            finishLabel={(count) => t("faceLock.scan.review.use", { count })}
            serverCheck={false}
            handoff={false}
            introCopy={{
              title: t("faceLock.sharpshoot.title"),
              lead: t("faceLock.sharpshoot.lead"),
              what: t("faceLock.sharpshoot.what", { count: planTotal(rescanPlan(parts)) }),
            }}
          />
        </React.Suspense>
      )}
    </>
  );
}
